import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { GlobalDataService } from './global-data.service';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { APPCONSTANTS } from './app-service.constants';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class HttpConfigInterceptor implements HttpInterceptor {
  private deviceInfo: any;
  private deviceId: any;
  private appInfo: any;
  // Guard: prevent multiple session-expired alerts when several requests 401 at once
  private isHandlingSessionExpiry = false;

  constructor(
    private alertController: AlertController,
    private toastController: ToastController,
    private globalData: GlobalDataService,
    private router: Router,
    // Use Injector to lazily load AuthenticationService and break the
    // potential circular dependency: interceptor → AuthService → AppService → HttpClient → interceptor
    private injector: Injector
  ) {}

  async showSessionTimeOutAlert() {
    const alert = await this.alertController.create({
      header: 'Alert',
      message: 'Your Session Expired',
      buttons: [
        {
          text: 'OK',
          handler: () => {
            this.router.navigate(['landing']);
          },
        },
      ],
    });
    alert.present();
  }

  private async handleSessionExpired(): Promise<void> {
    if (this.isHandlingSessionExpiry) return;
    this.isHandlingSessionExpiry = true;

    // Show a toast immediately so the user knows why they're being redirected
    const toast = await this.toastController.create({
      message: 'Your session has expired. Please log in again.',
      duration: 3000,
      position: 'top',
      color: 'warning',
      icon: 'lock-closed',
    });
    await toast.present();

    // Wait briefly so the user can read the message before the page changes
    setTimeout(async () => {
      try {
        // Lazy-load AuthenticationService to avoid circular DI at construction time
        const { AuthenticationService } = await import('./authentication.service');
        const authService = this.injector.get(AuthenticationService);
        await authService.logout();
      } catch {
        // Fallback: navigate to landing even if logout API call fails
        this.router.navigate(['landing']);
      } finally {
        this.isHandlingSessionExpiry = false;
      }
    }, 1500);
  }

  private logDeviceInfo = async () => {
    this.deviceInfo = await Device.getInfo();


    this.deviceId = (await Device.getId()).identifier;
    if (Capacitor.getPlatform() !== APPCONSTANTS.WEB) {

      this.appInfo = await App.getInfo();
    }

  };
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.logDeviceInfo();
    let plt: string = "" + this.deviceInfo?.platform;
    let deviceId: string = "" + this.deviceId;
    var headers;


    if (Capacitor.getPlatform() === APPCONSTANTS.WEB) {
      headers = {
        'Content-Type': 'application/json',
        'Platform': plt,
        'DeviceId': deviceId,
        // 'AppVersion': this.appInfo?.version != undefined ? this.appInfo?.version : 'Web',
        'DeviceInfo': this.deviceInfo != undefined ? JSON.stringify(this.deviceInfo) : '',
      }
    }
    else {


      headers = {
        'Content-Type': 'application/json',
        'Platform': plt,
        'DeviceId': deviceId,
        'AppVersion': this.appInfo?.version != undefined ? this.appInfo?.version : '',
        'DeviceInfo': this.deviceInfo != undefined ? JSON.stringify(this.deviceInfo) : '',
      }
    }

    request = request.clone({
      setHeaders: headers
    });
    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (event.status === 403) {
            this.showSessionTimeOutAlert();
          } else {
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (!environment.production) {
          console.error('HTTP Error:', error.status, error.url);
        }

        // Session expired — show a clear message and log the user out.
        // Business-logic 401s (e.g. ncpblockedusercheck) must not trigger logout.
        const requestUrl = error.url ?? request.url ?? '';
        const isBusinessAuthError = requestUrl.includes('ncpblockedusercheck');
        if ([401, 403].includes(error.status) && !isBusinessAuthError) {
          this.handleSessionExpired();
        }

        const rawBody = error.error;
        let body: any = rawBody;
        if (typeof rawBody === 'string') {
          try { body = JSON.parse(rawBody); } catch {}
        }
        const specificError = Array.isArray(body)
          ? body.find((e: any) => e?.['error.specific'])?.['error.specific']
          : body?.['error.specific'];
        return throwError(() => ({
          status: error.status,
          message: error.status === 0 ? 'Network Error' : (body?.message ?? 'Request Failed'),
          'error.specific': specificError,
          errorMessageEnglish: body?.errorMessageEnglish,
          errorMessageSpanish: body?.errorMessageSpanish,
        }));
      })
    );
  }
}
